/**
 * Publish a report to the project's client organisation, with its email (decisions 883 and 884;
 * docs/specs/cloud/app-email.md §8; help `cloud.app-email`): whether the client's people are emailed, what the email
 * carries (the link, the PDF attached, or both), for a weekly report the day's daily report too, and the subject and
 * message, which start as the project's (Set-up tab) and can be changed for this report only. The choice goes with
 * Publish (`report_publication.email_options`); the database queues the emails.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  latestPublishedDaily,
  reportEmailOptionsDefault,
  reportEmailOptionsToKeep,
  reportEmailValues,
  reportEmailWording,
  reportEmailWordingProblems,
  reportPublishedNotice,
  staffReportLabel,
  type PublishableReport,
  type ReportEmailContent,
  type ReportEmailSetting,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { loadReportEmailSetting, loadTenantName } from '../email/api.ts';
import { ReportEmailContentField, ReportEmailWords } from '../email/ReportEmailWords.tsx';
import { setPublished, type PortalProject, type PortalReport } from './api.ts';

export interface PublishDialogProps {
  readonly sb: SupabaseClient;
  readonly report: PortalReport;
  readonly project: PortalProject;
  /** The project's reports (the published daily reports a weekly one can carry). */
  readonly reports: readonly PortalReport[];
  /** Closed: what was done, in words, or null when cancelled. */
  readonly onClose: (done: string | null) => void;
  /** The project's setting (tests). */
  readonly loadSetting?: () => Promise<ReportEmailSetting | null>;
}

const publishable = (r: PortalReport): PublishableReport => ({
  id: r.id,
  kind: r.kind,
  period: r.period,
  revision: r.revision,
  published: Boolean(r.publication?.published),
});

export function PublishDialog(props: PublishDialogProps): ReactElement {
  const { sb, report, project, onClose } = props;
  // staff words: which revision is being published (R139: the client's email and list carry no revision number)
  const label = staffReportLabel(report);
  const [setting, setSetting] = useState<ReportEmailSetting | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [send, setSend] = useState(true);
  const [content, setContent] = useState<ReportEmailContent>('link');
  const [withDaily, setWithDaily] = useState(false);
  const [dailyId, setDailyId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);

  // this project's reports only (the Reports page lists every project's)
  const mine = useMemo(
    () => props.reports.filter((r) => r.projectId === project.id),
    [props.reports, project.id],
  );
  const dailies = useMemo(
    () =>
      props.reports
        .filter((r) => r.projectId === project.id && r.kind === 'daily' && r.publication?.published)
        .sort((a, b) => b.period.localeCompare(a.period) || b.revision - a.revision)
        .slice(0, 14),
    [props.reports, project.id],
  );

  useEffect(() => {
    let live = true;
    (props.loadSetting ?? (() => loadReportEmailSetting(sb, project.id)))()
      .then((s) => {
        if (!live) return;
        const o = reportEmailOptionsDefault(s, report.kind, mine.map(publishable));
        const w = reportEmailWording(s);
        setSetting(s);
        setSend(o.send);
        setContent(o.content);
        setWithDaily(o.daily_report_id !== null);
        setDailyId(o.daily_report_id ?? latestPublishedDaily(mine.map(publishable))?.id ?? '');
        setSubject(w.subject);
        setMessage(w.message);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    void loadTenantName(sb, project.tenantId).then((n) => live && setTenantName(n));
    return () => {
      live = false;
    };
    // the dialog reads the setting once, when it opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sb, project.id, project.tenantId, report.id]);

  async function publish(): Promise<void> {
    const p: string[] = send ? reportEmailWordingProblems({ subject, message }) : [];
    if (send && report.kind === 'weekly' && withDaily && !dailyId)
      p.push('Choose the daily report to include, or untick it.');
    setProblems(p);
    if (p.length > 0) return;
    const options = reportEmailOptionsToKeep(
      {
        send,
        subject,
        message,
        content,
        daily_report_id: report.kind === 'weekly' && withDaily ? dailyId : null,
      },
      setting,
      report.kind,
    );
    setBusy(true);
    try {
      const r = await setPublished(sb, report.id, true, options);
      const words = reportPublishedNotice({ label, clientOrgName: project.clientOrgName, options });
      onClose(
        r.emailChoiceKept
          ? words
          : `${label} published to ${project.clientOrgName ?? 'the client'}. Its people are emailed with the project's usual email: Teslyn Cloud's database update for email choices is not applied yet.`,
      );
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  }

  const projectWords = reportEmailWording(setting);
  return (
    <div className="panel confirm stack email-dialog" role="dialog" aria-labelledby="publish-dialog-title">
      <h2 className="heading" id="publish-dialog-title">
        {`Publish ${label}`}
      </h2>
      <p className="caption muted">
        {`${project.clientOrgName ?? 'The client'} sees it in Teslyn Cloud as soon as it is published.`}
      </p>
      {error && <ErrorState message={error} />}
      {!error && setting === undefined && <Loading what="the project's report emails" />}
      {setting !== undefined && (
        <>
          <CheckField
            label={`Email ${project.clientOrgName ?? 'the client'}'s people about it`}
            checked={send}
            onChange={setSend}
          />
          {send && (
            <>
              <ReportEmailContentField value={content} onChange={setContent} />
              {report.kind === 'weekly' && (
                <div className="stack">
                  <CheckField
                    label="Also include the day's daily report (its link and PDF)"
                    checked={withDaily}
                    onChange={setWithDaily}
                  />
                  {withDaily &&
                    (dailies.length > 0 ? (
                      <SelectField
                        label="Daily report"
                        value={dailyId}
                        onChange={setDailyId}
                        options={dailies.map((d) => [d.id, staffReportLabel(d)] as const)}
                      />
                    ) : (
                      <p className="caption muted">
                        No daily report of this project is published to the client yet: publish it first.
                      </p>
                    ))}
                </div>
              )}
              <ReportEmailWords
                subject={subject}
                message={message}
                onSubject={setSubject}
                onMessage={setMessage}
                fallback={{ ...projectWords, name: "the project's wording" }}
                preview={reportEmailValues({
                  kind: report.kind === 'weekly' ? 'weekly' : 'daily',
                  period: report.period,
                  revision: report.revision,
                  projectNumber: project.number,
                  projectTitle: project.title,
                  tenantName: tenantName ?? 'your organisation',
                  clientOrgName: project.clientOrgName ?? 'the client',
                  recipientName: "(the person's name)",
                  publisherName: '(you)',
                  cloudUrl: '',
                })}
              />
              <p className="caption muted">
                These words are for this report only; the project&apos;s own are on its Set-up tab.
              </p>
            </>
          )}
        </>
      )}
      {problems.length > 0 && (
        <ul className="notice notice--fail" role="alert">
          {problems.map((p) => (
            <li key={p} className="body">
              {p}
            </li>
          ))}
        </ul>
      )}
      <div className="row row--wrap">
        <Button
          variant="primary"
          busy={busy}
          disabled={setting === undefined && !error}
          onClick={() => void publish()}
        >
          {busy ? 'Publishing…' : 'Publish'}
        </Button>
        <Button variant="quiet" onClick={() => onClose(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
